import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Search, ShieldCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CarrierRiskTab } from "@/components/CarrierRiskTab";

const apiErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const CLAIM = {
  "": ["No claim", "bg-muted text-muted-foreground"],
  open: ["Claim open", "bg-red-500/15 text-red-600 dark:text-red-400"],
  in_review: ["In review", "bg-amber-500/15 text-amber-700 dark:text-amber-400"],
  settled: ["Settled", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"],
};

const ClaimBadge = ({ s, testid }) => {
  const [label, cls] = CLAIM[s] || [s, "bg-muted text-muted-foreground"];
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`} data-testid={testid}>{label}</span>;
};

const Policy = ({ r }) => (r.policy_no || r.insurance_company ? (
  <div>
    <div className="font-semibold">{r.policy_no || "—"}</div>
    <div className="text-xs text-muted-foreground">{[r.insurance_company, r.policy_date].filter(Boolean).join(" · ")}</div>
  </div>
) : <span className="text-xs text-muted-foreground">Not on LR yet</span>);

const Stat = ({ label, value, testid }) => (
  <div className="border border-border bg-card p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-heading font-bold text-xl mt-1 font-mono-num">{value}</div>
  </div>
);

export default function InsurancePage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`/insurance${q ? `?search=${encodeURIComponent(q)}` : ""}`)
        .then((r) => setRows(r.data))
        .catch((e) => toast.error(apiErr(e)))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const totals = useMemo(() => ({
    value: rows.reduce((s, r) => s + r.insured_value, 0),
    premium: rows.reduce((s, r) => s + r.premium, 0),
    claims: rows.filter((r) => ["open", "in_review"].includes(r.damage_status)).length,
  }), [rows]);

  return (
    <div className="space-y-4" data-testid="insurance-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-primary font-bold mb-1">Risk Cover</div>
        <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight uppercase flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" /> Insurance
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Every insured order with its policy, insured value, premium and damage claim status.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Insured orders" value={rows.length} testid="ins-stat-count" />
        <Stat label="Total insured value" value={inr(totals.value)} testid="ins-stat-value" />
        <Stat label="Total premium" value={inr(totals.premium)} testid="ins-stat-premium" />
        <Stat label="Active claims" value={totals.claims} testid="ins-stat-claims" />
      </div>

      <Tabs defaultValue="insured" className="space-y-4">
        <TabsList className="rounded-none">
          <TabsTrigger value="insured" className="rounded-none" data-testid="ins-tab-insured">Insured Orders</TabsTrigger>
          <TabsTrigger value="carrier" className="rounded-none" data-testid="ins-tab-carrier">Carrier Risk Receipts</TabsTrigger>
        </TabsList>
        <TabsContent value="insured" className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order no, customer, phone, policy no…" className="rounded-none bg-card pl-9 h-10" data-testid="insurance-search" />
          </div>
          <InsuranceTable rows={rows} loading={loading} />
          <InsuranceCards rows={rows} loading={loading} />
        </TabsContent>
        <TabsContent value="carrier"><CarrierRiskTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const InsuranceTable = ({ rows, loading }) => (
  <div className="hidden md:block border border-border bg-card overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted">
        <tr>
          <th className="px-4 py-2.5 text-left">Order</th>
          <th className="px-4 text-left">Customer / Route</th>
          <th className="px-4 text-left">Policy</th>
          <th className="px-4 text-right">Insured value</th>
          <th className="px-4 text-right">Premium</th>
          <th className="px-4 text-left">Damage claim</th>
        </tr>
      </thead>
      <tbody>
        {loading && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">Loading insured orders…</td></tr>}
        {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground" data-testid="insurance-empty">No insured orders found.</td></tr>}
        {!loading && rows.map((r) => (
          <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`insurance-row-${r.id}`}>
            <td className="px-4 py-3">
              <div className="font-bold font-mono-num">{r.order_number}</div>
              <div className="text-xs text-muted-foreground">{r.lr_numbers.join(", ") || "No LR"}</div>
            </td>
            <td className="px-4"><div className="font-semibold">{r.customer_name}</div><div className="text-xs text-muted-foreground">{r.route}</div></td>
            <td className="px-4"><Policy r={r} /></td>
            <td className="px-4 text-right font-mono-num">{inr(r.insured_value)}</td>
            <td className="px-4 text-right font-mono-num">{inr(r.premium)}{r.insurance_percent != null && <div className="text-[10px] text-muted-foreground">@ {r.insurance_percent}%</div>}</td>
            <td className="px-4">
              <ClaimBadge s={r.damage_status} testid={`insurance-claim-${r.id}`} />
              {r.damage_amount > 0 && <div className="text-xs text-muted-foreground mt-1 font-mono-num">{inr(r.damage_amount)}</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const InsuranceCards = ({ rows, loading }) => (
  <div className="md:hidden space-y-2">
    {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
    {!loading && rows.length === 0 && <div className="p-6 text-center text-muted-foreground text-sm border border-border bg-card">No insured orders found.</div>}
    {!loading && rows.map((r) => (
      <div key={r.id} className="border border-border bg-card p-3 space-y-2" data-testid={`insurance-card-${r.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div><div className="font-bold font-mono-num">{r.order_number}</div><div className="text-xs text-muted-foreground">{r.customer_name} · {r.route}</div></div>
          <ClaimBadge s={r.damage_status} />
        </div>
        <Policy r={r} />
        <div className="flex justify-between text-xs"><span className="text-muted-foreground">Insured value</span><span className="font-mono-num font-semibold">{inr(r.insured_value)}</span></div>
        <div className="flex justify-between text-xs"><span className="text-muted-foreground">Premium</span><span className="font-mono-num font-semibold">{inr(r.premium)}</span></div>
      </div>
    ))}
  </div>
);
